import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatCartPrice, formatCartSubtotal } from '../../util/cartDisplay';
import { formatVariantSummary } from '../../util/productVariants';
import { closeMiniCart, lineIdOf, selectCartCount, selectCartItems } from '../../ducks/cart.duck';
import { CUSTOMER_USER_TYPE } from '../../config/userRoles';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { pathByRouteName } from '../../util/routes';

import IconCart from '../IconCart/IconCart';
import IconClose from '../IconClose/IconClose';
import NamedLink from '../NamedLink/NamedLink';

import css from './MiniCart.module.css';

/**
 * MiniCart
 *
 * A drawer that slides in from the right after something is added to the
 * basket. It confirms what was added and offers the two next steps — keep
 * shopping, or go to the basket / checkout — without leaving the page.
 *
 * It reads everything from the cart duck: ADD_TO_CART opens it and records
 * which line was added. Rendered once, from the Topbar.
 */
const MiniCart = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const routeConfiguration = useRouteConfiguration();
  const { miniCartOpen, lastAddedLineId } = useSelector(state => state.cart);
  const items = useSelector(selectCartItems);
  const count = useSelector(selectCartCount);
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const closeButtonRef = useRef(null);
  const { pathname } = useLocation();

  const onClose = () => dispatch(closeMiniCart());

  // Leaving the page (e.g. with the browser's back button) closes the drawer.
  useEffect(() => {
    dispatch(closeMiniCart());
  }, [pathname, dispatch]);

  useEffect(() => {
    if (!miniCartOpen) {
      return undefined;
    }
    closeButtonRef.current?.focus();
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        dispatch(closeMiniCart());
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [miniCartOpen, dispatch]);

  if (!miniCartOpen) {
    return null;
  }

  const addedItem = items.find(item => lineIdOf(item) === lastAddedLineId);
  const subtotal = formatCartSubtotal(intl, items);
  const variantSummary = formatVariantSummary(addedItem?.productVariants);

  // Signed-out shoppers go to log in first (with a sign-up tab one tap away)
  // and come straight back to checkout afterwards.
  const checkoutPath = pathByRouteName('CartCheckoutPage', routeConfiguration);
  const checkoutLinkProps = isAuthenticated
    ? { name: 'CartCheckoutPage' }
    : {
        name: 'LoginForUserTypePage',
        params: { userType: CUSTOMER_USER_TYPE },
        to: { state: { from: checkoutPath } },
      };

  return (
    <div className={css.root}>
      <div className={css.backdrop} onClick={onClose} aria-hidden="true" />
      <aside
        className={css.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mini-cart-heading"
      >
        <div className={css.header}>
          <h2 id="mini-cart-heading" className={css.heading}>
            <FormattedMessage id="MiniCart.heading" />
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            className={css.closeButton}
            onClick={onClose}
            aria-label={intl.formatMessage({ id: 'MiniCart.close' })}
          >
            <IconClose rootClassName={css.closeIcon} />
          </button>
        </div>

        {addedItem ? (
          <div className={css.addedItem}>
            {addedItem.imageURL ? (
              <img className={css.addedImage} src={addedItem.imageURL} alt="" />
            ) : (
              <div className={css.addedImagePlaceholder}>
                <IconCart className={css.addedImagePlaceholderIcon} />
              </div>
            )}
            <div className={css.addedInfo}>
              {addedItem.authorName ? (
                <span className={css.addedBrand}>{addedItem.authorName}</span>
              ) : null}
              <span className={css.addedTitle}>{addedItem.title}</span>
              {variantSummary ? <span className={css.addedMeta}>{variantSummary}</span> : null}
              <span className={css.addedMeta}>
                <FormattedMessage
                  id="MiniCart.quantityInBasket"
                  values={{ quantity: addedItem.quantity }}
                />
              </span>
            </div>
            <span className={css.addedPrice}>
              {formatCartPrice(intl, addedItem.price, addedItem.quantity)}
            </span>
          </div>
        ) : null}

        <div className={css.summary}>
          <span>
            <FormattedMessage id="MiniCart.subtotal" values={{ count }} />
          </span>
          {subtotal ? <span className={css.summaryValue}>{subtotal}</span> : null}
        </div>
        <p className={css.deliveryNote}>
          <FormattedMessage id="MiniCart.deliveryNote" />
        </p>

        {/* Any of the three closes the drawer; the links then navigate as usual. */}
        <div className={css.actions} onClick={onClose}>
          <NamedLink {...checkoutLinkProps} className={css.checkoutButton}>
            <FormattedMessage id="MiniCart.checkout" />
          </NamedLink>
          <NamedLink name="CartPage" className={css.viewBasketButton}>
            <FormattedMessage id="MiniCart.viewBasket" />
          </NamedLink>
          <button type="button" className={css.continueButton}>
            <FormattedMessage id="MiniCart.continueShopping" />
          </button>
        </div>
      </aside>
    </div>
  );
};

export default MiniCart;
